<template>
    <div class="block-play-receipt">
        <div class="block-play-receipt__inner">
            

            <template v-if="isLoading">
                <div class="block-play-receipt__loading-wrapper">
                    <div class="block-play-receipt__loading-content">
                        <div class="block-play-receipt__loading-icon">
                            <div class="block-play-receipt__loading-icon-inner">
                                <IonIcon icon-name="receipt" icon-size="54px" />
                            </div>
                        </div>

                        <AtomTextHeading
                            v-if="title"
                            html-tag="h2"
                            :text="checkReceiptTitle"
                            class="block-play-codes__title"
                            alignment="center"
                            font-size="h2"
                        />
            
                        <div class="block-play-receipt__loading-bar">
                            <div class="block-play-receipt__loading-bar-inner" :style="{ width: `${loadingProgress}%` }" />
                        </div>

                        <div class="block-play-receipt__loading-text">
                            <AtomTextRichtext 
                            v-if="loadingText"
                            :html="loadingText" 
                            alignment="center" 
                            />
                        </div>
                    </div>

                    <div class="block-play-receipt__facts">
                        <DotLottieVue 
                            class="block-play-receipt__lottie"
                            src="/animations/scan-win.json" 
                            :autoplay="true" 
                            :loop="true" 
                            style="width: 1200px;"
                        />
                        
                        <TransitionGroup name="slide-up">
                            <div
                                v-for="fact of currentRandomFact"
                                :key="fact"
                                class="block-play-receipt__facts-fact"
                            >
                                {{ fact }}
                            </div>
                        </TransitionGroup>
                    </div>
                </div>
            </template>

            <template v-else>
                <AtomTextHeading
                    v-if="title"
                    html-tag="h2"
                    :text="title"
                    class="block-play-codes__title"
                    font-size="h2"
                />
    
                <AtomTextRichtext
                    v-if="text"
                    :html="text"
                    class="block-play-receipt__text"
                />
                
                <div class="block-play-receipt__upload">
                    <AtomInputImages
                        name="receipt"
                        ref="receiptInputRef"
                        class="block-play-receipt__upload-input"
                        :button-text="buttonText"
                        :button-upload-text="buttonUploadText"
                        :dropzone-text="dropzoneText"
                        accept="image/*,application/pdf"
                        :error-text="validationErrorText"
                        :validations="imageValidations"
                        @on-submit="onSubmit"
                    />
                </div>
            </template>

        </div>

        <transition name="fade-in">
            <AtomPopoverNotification
                v-if="popupVisible"
                position="fixed"
                :title="errorTitle"
                :text="errorMessage"
                :use-click-outside="true"
                type="error"
                @on-hide="popupVisible = false"
            />
        </transition>
    </div>
</template>

<script setup>
import { DotLottieVue } from "@lottiefiles/dotlottie-vue";

const props = defineProps({
    title: {
        type: String,
        default: '',
    },

    checkReceiptTitle: {
        type: String,
        default: 'Wir prüfen deinen Beleg',
    },

    text: {
        type: String,
        default: '',
    },

    loadingText: {
        type: String,
        default: 'Jetzt wird es spannend.',
    },

    buttonText: {
        type: String,
        default: '',
    },

    buttonUploadText: {
        type: String,
        default: '',
    },

    dropzoneText: {
        type: String,
        default: '',
    },

    errorTitleGeneral: {
        type: String,
        default: '',
    },
    errorTitleDuplicate: {
        type: String,
        default: '',
    },
    errorTitleNotReadable: {
        type: String,
        default: '',
    },
    errorMessageNotReadable: {
        type: String,
        default: '',
    },
    errorMessageDuplicate: {
        type: String,
        default: '',
    },
    errorMessageGeneral: {
        type: String,
        default: '',
    },
    validationErrorText: {
        type: String,
        default: '',
    },
    availableFacts: {
        type: Array,
        default: () => [],
    },

    imageValidations: {
        type: Array,
        default: () => [],
    },
});

const isLoading = ref(false);
const receiptInputRef = ref(null);
const popupVisible = ref(false);
const errorMessage = ref(props.errorMessageGeneral);
const errorTitle = ref(props.errorTitleGeneral);
const localePath = useLocalePath();
const loadingProgress = ref(0);

const EXPECTED_PROGRESS_DURATION_MS = 70_000;
const PROGRESS_CEILING = 97;
const FINISH_MIN_DURATION_MS = 180;
const FINISH_MAX_DURATION_MS = 600;
const FACT_ROTATION_INTERVAL_MS = 5_000;

const progressStartedAt = ref(0);
const isProgressRunning = ref(false);
const currentRandomFact = ref([]);
const normalizedAvailableFacts = computed(() => (
    props.availableFacts.filter((fact) => typeof fact === 'string' && fact.trim().length > 0)
));

const { pause: pauseProgressRaf, resume: resumeProgressRaf } = useRafFn(() => {
    if (!isProgressRunning.value) return;

    const elapsed = Date.now() - progressStartedAt.value;
    const normalizedTime = Math.min(elapsed / EXPECTED_PROGRESS_DURATION_MS, 1);

    const easedProgress = 1 - ((1 - normalizedTime) ** 3);
    const nextProgress = Math.min(PROGRESS_CEILING, easedProgress * PROGRESS_CEILING);

    if (nextProgress > loadingProgress.value) {
        loadingProgress.value = nextProgress;
    }
}, {
    immediate: false,
});

const stopProgressAnimation = () => {
    isProgressRunning.value = false;
    pauseProgressRaf();
};

const getRandomFact = (excludeFact = '') => {
    if (!normalizedAvailableFacts.value.length) return '';
    if (normalizedAvailableFacts.value.length === 1) return normalizedAvailableFacts.value[0];

    const possibleFacts = normalizedAvailableFacts.value.filter((fact) => fact !== excludeFact);
    const factsPool = possibleFacts.length ? possibleFacts : normalizedAvailableFacts.value;
    const randomIndex = Math.floor(Math.random() * factsPool.length);

    return factsPool[randomIndex];
};

const setNextRandomFact = () => {
    if(!currentRandomFact.value.length) {
        currentRandomFact.value = [getRandomFact()];
        return;
    }

    currentRandomFact.value.splice(0, 1);
    currentRandomFact.value.push(getRandomFact());
};

const { pause: pauseFactRotation, resume: resumeFactRotation } = useIntervalFn(() => {
    setNextRandomFact();
}, FACT_ROTATION_INTERVAL_MS, {
    immediate: false,
    immediateCallback: false,
});

const startFactRotation = () => {
    pauseFactRotation();
    setNextRandomFact();
    resumeFactRotation();
};

const stopFactRotation = () => {
    pauseFactRotation();
};

const startProgressAnimation = () => {
    stopProgressAnimation();
    loadingProgress.value = 0;
    progressStartedAt.value = Date.now();
    isProgressRunning.value = true;
    resumeProgressRaf();
};

const finishProgressAnimation = async () => {
    stopProgressAnimation();

    if (loadingProgress.value >= 100) return;

    const remainingProgress = 100 - loadingProgress.value;
    const finishDuration = Math.max(
        FINISH_MIN_DURATION_MS,
        Math.min(FINISH_MAX_DURATION_MS, remainingProgress * 20),
    );

    loadingProgress.value = 100;

    await new Promise((resolve) => {
        setTimeout(resolve, finishDuration);
    });
};

onBeforeUnmount(() => {
    stopProgressAnimation();
    stopFactRotation();
});

const onSubmit = async (fileArray) => {
    
    if (!fileArray) return;
    isLoading.value = true;
    startProgressAnimation();
    startFactRotation();

    sendEvent({
        event: 'receipt_scan',
        category: 'scan-receipt',
        result: 'submitted',
    });

    try {
        const result = await usePlayReceipt({
            files: fileArray,
        });

        const receiptDocumentIdCookie = useCookie('receiptDocumentId');
        receiptDocumentIdCookie.value = result.receipt_document_id;

        let redirect = null;

        
        switch(result.result) {
            case 'win':
                redirect = '/wheel-of-fortune';
                sendEvent({
                    event: 'receipt_scan',
                    category: 'scan-receipt',
                    result: 'win',
                });
                break;
            case 'no_win':
                redirect = '/no-win';
                sendEvent({
                    event: 'receipt_scan',
                    category: 'scan-receipt',
                    result: 'no_win',
                });
                break;
            case 'no_valid_products':
                // redirect = '/no-valid-products';
                redirect = '/no-win';
                sendEvent({
                    event: 'receipt_scan',
                    category: 'scan-receipt',
                    result: 'no_valid_products',
                });
                break;
        }

        if(!redirect) {
            throw new Error('Invalid result');
        }

        return navigateTo(localePath(redirect));
    } catch (e) {
        await navigateTo(localePath('/no-win'));
        // const backendMessage =
        //     e?.data?.error?.message ||
        //     e?.data?.data?.error?.message ||
        //     e?.data?.message ||
        //     e?.data?.statusMessage ||
        //     e?.statusMessage ||
        //     e?.message ||
        //     'Unknown error';


        // switch (backendMessage) {
        //     case 'duplicate':
        //         errorMessage.value = props.errorMessageDuplicate;
        //         errorTitle.value = props.errorTitleDuplicate;
        //         break;
        //     case 'not_readable':
        //         errorMessage.value = props.errorMessageNotReadable;
        //         errorTitle.value = props.errorTitleNotReadable;
        //         break;
        //     default:
        //         errorMessage.value = props.errorMessageGeneral;
        //         errorTitle.value = props.errorTitleGeneral;
        //         break;
        // }
    
        // if(backendMessage === 'general') {
        //     useSentryError(e, false);
        // }

        // if(receiptInputRef.value) {
        //     receiptInputRef.value.resetValue();
        // }

        // popupVisible.value = true;
    } finally {
        await finishProgressAnimation();
        stopFactRotation();
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
.block-play-receipt {
    @include wrapper-layout;
    @include grid-layout();
 
}


.block-play-receipt__inner {
    @include box-default;
    @include default-content-columns('medium-narrow');
    @include fluid('row-gap', 20px, 15px, 20px);

    display: flex;
    flex-direction: column;
}

.block-play-receipt__upload {
    display: flex;
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    min-height: 276px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px dashed var(--c-primary);
    border-radius: var(--b-radius-700);
    background-color: var(--c-primary-200);
    row-gap: 23px;
    
}

.block-play-receipt__lottie {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    height: 100%;
    aspect-ratio: 539 / 276;
    object-fit: cover;
    transform: scale(1.075);
}

.slide-up-move, /* apply transition to moving elements */
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.5s ease;
}

.slide-up-enter-from {
    opacity: 0;
    transform: translateY(30px);
}

.slide-up-leave-to {
    opacity: 0;
    transform: translateY(-30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.slide-up-leave-active {
    position: absolute;
}   

.block-play-receipt__facts {
    position: relative;
    display: flex;
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px solid var(--c-white);
    border-radius: var(--b-radius-700);
    aspect-ratio: 539 / 276;
}


.block-play-receipt__facts-fact {
    position: absolute;
    top: 20px;
    right: 20px;
    bottom: 20px;
    left: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: auto;
    color: var(--c-white);
    font-family: var(--f-family--primary);
    font-size: var(--f-size--p);
    font-weight: var(--f-weight--400);
    line-height: var(--l-height--p);
    text-align: center;
}

.block-play-receipt__submit {
    display: flex;
    align-items: center;
    justify-content: center;
}

.block-play-receipt__upload-input {
    border-radius: var(--b-radius-700);
}

.block-play-receipt__loading-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 60px;
}

.block-play-receipt__loader {
    position: fixed;
    z-index: 1000;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(5px);
    background: rgba(255, 255, 255, 0.95);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease-in-out;

    &.is-visible {
        opacity: 1;
        pointer-events: all;
    }
}

.block-play-receipt__loading-content {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 20px;
}

.block-play-receipt__loading-bar {
    display: flex;
    width: 100%;
    height: 6px;    
    border-radius: 3px;
    background-color: var(--c-primary-200);
}

.block-play-receipt__loading-bar-inner {
    width: 0;
    height: 100%;
    border-radius: 3px;
    background-color: var(--c-primary-500);
    transition: width 140ms linear;
}

.block-play-receipt__loading-icon {
    display: flex;
    justify-content: center;
}

.block-play-receipt__loading-icon-inner {
    display: flex;
    width: 80px;
    height: 80px;
    align-items: center;
    justify-content: center;
    border: 1px solid #FFF;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.48);
}
</style>